import React, { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { useUserDateFormatter } from '../../../../shared/hooks/useUserDateFormatter';
import { DIAM_GRID_PAGE_SIZE_OPTIONS } from '../../diamGridUtils';
import { useDiamGridPagination } from '../../hooks/useDiamGridPagination';
import { DiamDataGrid } from '../DiamDataGrid/DiamDataGrid';
import { DataGridContainer } from '../DiamDataGrid/styles';
import { buildAuditLogColumns } from './columns';
import { mapApiAuditLog } from './mapApiAuditLog';
import { useAuditLogs } from './useAuditLogs';

export function AuditLogsGrid() {
  const { enqueueSnackbar } = useSnackbar();
  const { formatDateTime } = useUserDateFormatter();
  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    handleServerFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = useDiamGridPagination();

  const auditLogsQuery = useAuditLogs({
    page: pageIndex,
    pageSize,
    sortModel,
    filterModel,
  });

  const columns = useMemo(
    () => buildAuditLogColumns(formatDateTime),
    [formatDateTime],
  );

  const rows = useMemo(
    () =>
      (auditLogsQuery.data?.elements ?? []).map(auditLog =>
        mapApiAuditLog(auditLog),
      ),
    [auditLogsQuery.data],
  );

  useEffect(() => {
    if (!auditLogsQuery.isError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(auditLogsQuery.error, 'Failed to load audit logs'),
      { variant: 'error' },
    );
  }, [auditLogsQuery.error, auditLogsQuery.isError, enqueueSnackbar]);

  return (
    <DataGridContainer>
      <DiamDataGrid
        title="Audit logs"
        searchPlaceholder="Search by name"
        rows={rows}
        columns={columns}
        loading={auditLogsQuery.isPending || auditLogsQuery.isFetching}
        getRowId={row => row.id}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleServerFilterModelChange}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        rowCount={auditLogsQuery.data?.total ?? 0}
        pageSizeOptions={[...DIAM_GRID_PAGE_SIZE_OPTIONS]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        disableRowSelectionOnClick
      />
    </DataGridContainer>
  );
}
